/**
 * aiConfig.ts — fetch + hold the LLM provider config from the backend.
 *
 * The SettingsModal AI tab, ChatModal, and AskBox all need the provider/model
 * menu. This hook fetches GET /api/ai/config once per backend connection and
 * exposes the available options. The chosen provider/model are owned by the app
 * (persisted via usePersistedState, same pattern as hz/theme) and passed down;
 * this hook is read-only metadata + the mutation helpers the AI tab calls.
 *
 * Keys are server-side — the config response never contains them. Providers
 * come from the AES-encrypted keystore DB (editable) and/or env vars
 * (read-only). `presets` prefills the Add form (z.ai / Gemini / OpenAI). The
 * provider form is always visible; `keystore` is false only when the backend's
 * cryptography extra is absent (writes then 503 and surface inline). When no
 * provider has a key, `available` is false and the UI shows an empty state.
 */
import { useCallback, useEffect, useState } from 'react';

export interface AiProvider {
  id: string;
  label: string;
  baseUrl: string;
  models: string[];
  defaultModel: string;
  /** Env-sourced providers are read-only in the UI (can't edit/delete). */
  readonly: boolean;
}

export interface AiPreset {
  id: string;
  label: string;
  baseUrl: string;
  model: string;
  models: string[];
}

export interface AiConfig {
  available: boolean;
  providers: AiProvider[];
  default: { provider: string; model: string } | null;
  /** Builtin templates (z.ai/Gemini/OpenAI) for the "Add provider" picker. */
  presets: AiPreset[];
  /** True when the keystore is enabled (DB providers can be managed). */
  keystore: boolean;
}

interface AiConfigState {
  config: AiConfig | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

function authHeaders(token: string | null): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

async function errorText(r: Response): Promise<string> {
  const fallback = `request failed (${r.status})`;
  try {
    const j = (await r.json()) as { detail?: unknown };
    if (typeof j?.detail === 'string') return j.detail;
    if (j?.detail) return JSON.stringify(j.detail);
  } catch {
    /* ignore */
  }
  return fallback;
}

export function useAiConfig(
  backendOrigin: string | null,
  token: string | null,
): AiConfigState {
  const [config, setConfig] = useState<AiConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Bump to force a refetch (after a provider mutation).
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!backendOrigin) {
      setConfig(null);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`${backendOrigin}/api/ai/config`, { headers: authHeaders(token) })
      .then(async (r) => {
        if (!r.ok) throw new Error(await errorText(r));
        return (await r.json()) as AiConfig;
      })
      .then((cfg) => {
        if (!cancelled) {
          setConfig(cfg);
          setLoading(false);
          setError(null);
        }
      })
      .catch((e: Error) => {
        if (!cancelled) {
          setConfig(null);
          setLoading(false);
          setError(e.message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [backendOrigin, token, nonce]);

  return { config, loading, error, reload };
}

/**
 * Resolve the effective provider/model: the persisted choice, falling back to
 * the backend's default, falling back to the first provider's first model.
 * Returns null when no providers are configured.
 */
export function resolveChoice(
  config: AiConfig | null,
  provider: string | null,
  model: string | null,
): { provider: string; model: string } | null {
  if (!config || !config.available || config.providers.length === 0) return null;
  const chosenP =
    config.providers.find((p) => p.id === provider) ??
    config.providers.find((p) => p.id === config.default?.provider) ??
    config.providers[0];
  const chosenM =
    model && chosenP.models.includes(model)
      ? model
      : config.default?.model && chosenP.models.includes(config.default.model)
        ? config.default.model
        : chosenP.defaultModel || chosenP.models[0];
  return { provider: chosenP.id, model: chosenM };
}

// ─── Provider management (mutates the keystore DB) ─────────────────────────
// Each helper hits the backend route; callers should call `reload()` afterward
// so the hook re-fetches the authoritative config (the POST/PATCH response body
// is intentionally not threaded back — one code path).

export interface ProviderUpsertBody {
  id: string;
  label: string;
  baseUrl: string;
  apiKey: string;
  model: string;
  models?: string[];
}

export async function createAiProvider(
  backendOrigin: string,
  token: string | null,
  body: ProviderUpsertBody,
): Promise<void> {
  const r = await fetch(`${backendOrigin}/api/ai/providers`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(await errorText(r));
}

export async function updateAiProvider(
  backendOrigin: string,
  token: string | null,
  id: string,
  body: Partial<Omit<ProviderUpsertBody, 'id'>>,
): Promise<void> {
  const r = await fetch(`${backendOrigin}/api/ai/providers/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(await errorText(r));
}

export async function deleteAiProvider(
  backendOrigin: string,
  token: string | null,
  id: string,
): Promise<void> {
  const r = await fetch(`${backendOrigin}/api/ai/providers/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  if (!r.ok) throw new Error(await errorText(r));
}

export interface AiTestBody {
  /** Load a stored provider's key/url/model by id, then apply overrides. */
  provider?: string;
  baseUrl?: string;
  apiKey?: string;
  model?: string;
}

export interface AiTestResult {
  ok: boolean;
  detail?: string;
}

/**
 * Probe a provider connection with a one-token completion. Works on a stored
 * provider id (key resolved server-side) OR on raw fields (for testing an
 * unsaved form before committing it). The route returns 200 with `{ok:false,
 * detail}` on a provider error so the caller shows the message inline.
 */
export async function testAiProvider(
  backendOrigin: string,
  token: string | null,
  body: AiTestBody,
): Promise<AiTestResult> {
  const r = await fetch(`${backendOrigin}/api/ai/test`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(await errorText(r));
  return (await r.json()) as AiTestResult;
}

// ─── Chat embeddings (semantic search over chat history) ────────────────────
// A standalone OpenAI-compatible /embeddings endpoint — not tied to the chat
// providers, so a local Ollama can serve embeddings on its own.

export interface EmbeddingsConfig {
  enabled: boolean;
  baseUrl: string;
  model: string;
  hasApiKey: boolean;
}

/** Save body — apiKey omitted/blank keeps the stored key. */
export interface EmbeddingsDraft {
  enabled: boolean;
  baseUrl: string;
  model: string;
  apiKey?: string;
}

export async function fetchEmbeddingsConfig(
  backendOrigin: string,
  token: string | null,
): Promise<EmbeddingsConfig> {
  const r = await fetch(`${backendOrigin}/api/ai/embeddings`, { headers: authHeaders(token) });
  if (!r.ok) throw new Error(await errorText(r));
  return (await r.json()) as EmbeddingsConfig;
}

export async function saveEmbeddingsConfig(
  backendOrigin: string,
  token: string | null,
  body: EmbeddingsDraft,
): Promise<EmbeddingsConfig> {
  const r = await fetch(`${backendOrigin}/api/ai/embeddings`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(await errorText(r));
  return (await r.json()) as EmbeddingsConfig;
}

export interface EmbeddingsTestResult {
  ok: boolean;
  detail?: string;
  dimensions?: number;
  model?: string;
}

/** Embed a probe string to verify an endpoint BEFORE saving. */
export async function testEmbeddingsConfig(
  backendOrigin: string,
  token: string | null,
  body: { baseUrl?: string; model?: string; apiKey?: string },
): Promise<EmbeddingsTestResult> {
  const r = await fetch(`${backendOrigin}/api/ai/embeddings/test`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(await errorText(r));
  return (await r.json()) as EmbeddingsTestResult;
}
